import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { TOOL_CONST } from '@/constant';
import { getEnvValue } from '@/package';
import type { ToolSettingActions, ToolSettingState } from '@/type';

const INIT_STATE = {
  isDirectWork: false,
  cameraDistance: {
    init: 50,
    current: 50,
  },
  isFixedIntensity: true,
  isPcdPointColorVisible: true,
  gridShow: true,
  gridColor: getEnvValue('VITE_APP_DEFAULT_GRID_COLOR').replace('0x', '#'),
  gridTextColor: getEnvValue('VITE_APP_DEFAULT_GRID_COLOR').replace('0x', '#'),
  labelingPannelVisible: true,
  cameraPannelShow: true,
  imagePopupVisible: false,
  isShortTrackID: true,
  labelBoxTrackIDVisible: false,
  shortcutPopupVisible: false,
  stepSizeConfig: {
    dimension: TOOL_CONST.DEFAULT_TOOL_DIMENSION_STEP_SIZE,
    move: TOOL_CONST.DEFAULT_TOOL_MOVE_STEP_SIZE,
    rotate: TOOL_CONST.DEFAULT_TOOL_ROTATE_STEP_SIZE,
  },
  pointSize: TOOL_CONST.DEFAULT_TOOL_PCD_POINT_SIZE,
  pointSizeConfig: {
    step: TOOL_CONST.TOOL_PCD_POINT_STEP,
    min: TOOL_CONST.TOOL_PCD_POINT_MIN,
    max: TOOL_CONST.TOOL_PCD_POINT_MAX,
  },
  labelingBoxOpacities: {} as ToolSettingState['labelingBoxOpacities'],
  opacityConfig: {
    background: {
      min: TOOL_CONST.TOOL_LABELING_BOX.OPACITY.BOX.MIN,
      max: TOOL_CONST.TOOL_LABELING_BOX.OPACITY.BOX.MAX,
      step: TOOL_CONST.TOOL_LABELING_BOX.OPACITY.BOX.STEP,
      default: TOOL_CONST.TOOL_LABELING_BOX.OPACITY.BOX.DEFAULT,
    },
    border: {
      min: TOOL_CONST.TOOL_LABELING_BOX.OPACITY.LINE.MIN,
      max: TOOL_CONST.TOOL_LABELING_BOX.OPACITY.LINE.MAX,
      step: TOOL_CONST.TOOL_LABELING_BOX.OPACITY.LINE.STEP,
      default: TOOL_CONST.TOOL_LABELING_BOX.OPACITY.LINE.DEFAULT,
    },
  },
  toolSettingVisible: false,
  orbitInfo: {
    horizonReverse: false,
    verticalReverse: false,
  },
} as ToolSettingState;

const useToolSettingStore = create<ToolSettingState & ToolSettingActions>()(
  persist(
    (set, get) => {
      return {
        ...INIT_STATE,

        getCameraZoom() {
          const { cameraDistance } = get();

          return cameraDistance.current / cameraDistance.init;
        },
        getOpacityByLabelId(labelId) {
          return (
            get().labelingBoxOpacities[labelId] ?? {
              background: TOOL_CONST.TOOL_LABELING_BOX.OPACITY.BOX.DEFAULT,
              border: TOOL_CONST.TOOL_LABELING_BOX.OPACITY.LINE.DEFAULT,
            }
          );
        },

        setIsDirectWork(isDirectWork) {
          set({ isDirectWork });
        },
        setGridVisible(gridVisible) {
          set({ gridShow: gridVisible });
        },
        setGridColor(gridColor) {
          set({ gridColor });
        },
        setGridTextColor(gridTextColor) {
          set({ gridTextColor });
        },
        initLabelingBoxOpactity(labelIdList) {
          const current = get().labelingBoxOpacities;

          set({
            labelingBoxOpacities: labelIdList.reduce((acc, labelId) => {
              return {
                ...acc,
                [labelId]: current[labelId] ?? {
                  background: TOOL_CONST.TOOL_LABELING_BOX.OPACITY.BOX.DEFAULT,
                  border: TOOL_CONST.TOOL_LABELING_BOX.OPACITY.LINE.DEFAULT,
                },
              };
            }, {}),
          });
        },
        addLabelingBoxOpactity(labelId) {
          set({
            labelingBoxOpacities: {
              ...get().labelingBoxOpacities,
              [labelId]: {
                background: TOOL_CONST.TOOL_LABELING_BOX.OPACITY.BOX.DEFAULT,
                border: TOOL_CONST.TOOL_LABELING_BOX.OPACITY.LINE.DEFAULT,
              },
            },
          });
        },
        setLabelingBoxOpacityByProp(labelId, prop, value) {
          set({
            labelingBoxOpacities: {
              ...get().labelingBoxOpacities,
              [labelId]: {
                ...get().labelingBoxOpacities[labelId],
                [prop]: value,
              },
            },
          });
        },
        setPcdPointColorVisible(isPcdPointColorVisible) {
          set({ isPcdPointColorVisible });
        },
        setPointSize(pointSize) {
          set({
            pointSize,
          });
        },
        setOrbitStorageInfo(newOrbitStorageInfo) {
          const orbitInfo = {
            ...get().orbitInfo,
            ...newOrbitStorageInfo,
          };
          set({
            orbitInfo,
          });
        },
        setImagePopupVisible(imagePopupVisible) {
          set({ imagePopupVisible });
        },
        setIsShortTrackID(isShortTrackID) {
          set({ isShortTrackID });
        },
        setLabelBoxTrackIDVisible(labelBoxTrackIDVisible) {
          set({ labelBoxTrackIDVisible });
        },
        setShortcutPopupVisible(shortcutPopupVisible) {
          set({ shortcutPopupVisible });
        },
        setStepSizeConfig(key, value) {
          set({ stepSizeConfig: { ...get().stepSizeConfig, [key]: value } });
        },
        setToolSettingVisible(toolSettingVisible) {
          set({ toolSettingVisible });
        },

        toggleCameraPannel() {
          set({ cameraPannelShow: !get().cameraPannelShow });
        },
        toggleFixedIntensity() {
          set({ isFixedIntensity: !get().isFixedIntensity });
        },
        toggleGrid() {
          get().setGridVisible(!get().gridShow);
        },
        toggleImagePopupVisible() {
          get().setImagePopupVisible(!get().imagePopupVisible);
        },
        toggleLabelBoxTrackIDVisible() {
          get().setLabelBoxTrackIDVisible(!get().labelBoxTrackIDVisible);
        },
        togglePcdPointColorVisible() {
          get().setPcdPointColorVisible(!get().isPcdPointColorVisible);
        },
        toggleToolSettingVisible() {
          get().setToolSettingVisible(!get().toolSettingVisible);
        },
      };
    },
    {
      merge: (persistedState, currentState) => {
        const nextState = {
          ...currentState,
          ...(persistedState as Partial<ToolSettingState & ToolSettingActions>),
        };
        const persistedStepSizeConfig = (
          persistedState as Partial<ToolSettingState>
        )?.stepSizeConfig;
        const persistedDimension = persistedStepSizeConfig?.dimension;

        return {
          ...nextState,
          stepSizeConfig: {
            ...currentState.stepSizeConfig,
            ...persistedStepSizeConfig,
            dimension:
              typeof persistedDimension === 'number' &&
              Number.isFinite(persistedDimension) &&
              persistedDimension > 0
                ? persistedDimension
                : currentState.stepSizeConfig.dimension,
          },
        };
      },
      name: 'tool-setting-store',
    },
  ),
);

export default useToolSettingStore;
